import { createContext, PropsWithChildren, useCallback, useContext, useEffect, useState } from 'react';

import { UpdateTimeOffset } from './TimeContext.utils';

const TimeContext = createContext(0);

const ONE_MINUTE_IN_MS = 60 * 1000;
const HALF_A_MINUTE_IN_MS = 30 * 1000;

/**
 * EnvironmentProvider component that provides the specified environment to its children.
 */
export function TimeContextProvider({ children }: PropsWithChildren) {
	const [timeOffset, setTimeOffset] = useState(0);

	const updateOffset = useCallback(
		({ detail }: UpdateTimeOffset) => {
			// normalize details because we can set dates or time offsets
			let newTimeOffset: number;
			if ('date' in detail) {
				newTimeOffset = detail.date.getTime() - Date.now();
			} else {
				newTimeOffset = detail.timeOffset;
			}

			// only update the difference if it is more than one minute and the current difference is more than half a minute
			if (Math.abs(newTimeOffset) > ONE_MINUTE_IN_MS && Math.abs(newTimeOffset - timeOffset) > HALF_A_MINUTE_IN_MS) {
				setTimeOffset(newTimeOffset);
			}
		},
		[timeOffset]
	);

	useEffect(() => {
		window.addEventListener('br24.updateCurrentTime', updateOffset);

		return () => {
			window.removeEventListener('br24.updateCurrentTime', updateOffset);
		};
	}, [updateOffset]);

	return <TimeContext.Provider value={timeOffset}>{children}</TimeContext.Provider>;
}

/**
 * Gets the current time used on the backend to calculate the correct livestream states
 */
export function useCurrentDate() {
	const timeOffset = useContext(TimeContext);

	const localDate = Date.now();

	return new Date(localDate + timeOffset);
}

export function useTimeOffset() {
	return useContext(TimeContext);
}
